import { useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { KpiCard } from '../components/KpiCard'
import { Icon, type IconName } from '../components/Icon'
import { BarChart } from '../components/charts/BarChart'
import { ChartPeriodPicker } from '../components/ChartPeriodPicker'
import type { PeriodKey } from '../lib/period-context'
import { FMT_INT, relativeTime } from '../lib/money'
import { InvoiceActivityTab } from './InvoiceActivity'
import { useTranslation } from 'react-i18next'

// Map period key → days (for endpoints that only know days)
function daysFor(p: PeriodKey): number {
  switch (p) {
    case '7d': return 7
    case '30d': return 30
    case 'this_month': return 30
    case 'last_month': return 60
    case 'this_quarter': return 92
    case 'ytd': return 365
    case 'last_year': return 365
    case 'custom': return 30
    default: return 30
  }
}

interface Upload { id: string; file: string; carrier: string; rows: number; status: string; imported_at: string; age_minutes: number }
interface PerCarrier { code: string; rows: number; last_imported: string | null; meta: { name: string; color: string; icon: string } | null }

interface ScraperRun {
  id: string
  carrier: string
  started_at: string
  finished_at: string | null
  status: 'running' | 'success' | 'failed' | 'skipped'
  exit_code: number | null
  stdout: string
  stderr: string
  duration_ms: number | null
}

// Carriers that have scraper scripts (can be triggered manually)
const SCRAPER_CARRIERS = new Set(['fedex', 'ups', 'usps'])

interface PerFileResult {
  file: string
  ok: boolean
  carrier?: string
  label?: string
  exit_code?: number
  stdout?: string
  stderr?: string
  duration_ms?: number
  error?: string
}

interface UploadResult {
  ok: boolean
  is_zip?: boolean
  file_count?: number
  results?: PerFileResult[]
  // top-level convenience fields (mirror of results[0] for non-zip uploads)
  carrier?: string
  label?: string
  saved_to?: string
  exit_code?: number
  stdout?: string
  stderr?: string
  duration_ms?: number
  error?: string
  hint?: string
  filename?: string
  retries?: number
}

type Tab = 'upload' | 'activity'

export function UploadsView() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>('upload')
  const fileInput = useRef<HTMLInputElement | null>(null)
  const [picked, setPicked] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<Array<{ name: string; result: UploadResult }>>([])
  const [logCarrier, setLogCarrier] = useState<string | null>(null)
  const qc = useQueryClient()

  const runScraper = useMutation({
    mutationFn: async (carrier: string) => {
      const res = await fetch(`/api/scrapers/${carrier}/run`, { method: 'POST' })
      return res.json() as Promise<{ run_id: string; status: string }>
    },
    onMutate: (carrier) => {
      setLogCarrier(carrier)
    },
    onSettled: (_data, _err, carrier) => {
      qc.invalidateQueries({ queryKey: ['scraper-logs', carrier] })
    },
  })

  const { data: uploads } = useQuery<{
    summary: { files_14d: number; rows_24h: number; rejected_files: number } | null
    uploads: Upload[]
  }>({
    queryKey: ['uploads'],
    queryFn: () => fetch('/api/uploads').then(r => r.json()),
  })
  const { data: perCarrier } = useQuery<{ carriers: PerCarrier[] }>({
    queryKey: ['uploads-per-carrier'],
    queryFn: () => fetch('/api/uploads/per-carrier').then(r => r.json()),
  })
  const [volumePeriod, setVolumePeriod] = useState<PeriodKey>('30d')
  const { data: volume } = useQuery<{ volume: Array<{ day: string; n: number }> }>({
    queryKey: ['uploads-volume', volumePeriod],
    queryFn: () => fetch(`/api/uploads/volume?days=${daysFor(volumePeriod)}`).then(r => r.json()),
  })

  // '—' while loading or when the API could not read the figures, never a false 0
  const summary = uploads?.summary
  const lastUpload = uploads?.uploads[0]

  // Cap the ingestion-volume y-axis when a single outlier day (e.g. the initial
  // historical bulk import) would otherwise compress all regular daily bars to 0.
  // Strategy: compare max vs second-highest — if max > 3× second, cap at 150%
  // of second-highest so normal days are visible. p95 doesn't work here because
  // with ~20 data points Math.floor(n*0.95) lands on the last element (the spike).
  // The tooltip callback in BarChart always shows the real value even when clipped.
  const _volSorted = [...(volume?.volume?.map(v => v.n) ?? [])].sort((a, b) => a - b)
  const _volMax    = _volSorted[_volSorted.length - 1] ?? 0
  const _volSecond = _volSorted[_volSorted.length - 2] ?? 0
  const volYMax    = _volSorted.length >= 2 && _volMax > _volSecond * 3
    ? Math.ceil(_volSecond * 1.5)
    : undefined

  async function handlePicked(files: FileList | null) {
    if (!files || files.length === 0) return
    const arr = Array.from(files)
    setPicked(arr)
    setResults([])
    setUploading(true)

    const collected: Array<{ name: string; result: UploadResult }> = []
    for (const f of arr) {
      // Retry up to 3 times on connection failure ("Failed to fetch") — that
      // error usually means the browser tried to reuse a stale HTTP keep-alive
      // connection between sequential uploads. A fresh attempt opens a new
      // socket. HTTP error responses are NOT retried — they come back as JSON.
      let result: UploadResult = { ok: false, error: 'unknown' }
      let retries = 0
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const fd = new FormData()
          fd.append('file', f)
          const res = await fetch('/api/uploads', { method: 'POST', body: fd })
          result = await res.json().catch(() => ({ ok: false, error: 'Invalid JSON in response' }))
          result.retries = retries
          break
        } catch (e) {
          retries++
          if (attempt < 2) {
            // Brief pause so the browser can establish a fresh connection
            await new Promise(r => setTimeout(r, 600))
            continue
          }
          result = { ok: false, error: `${(e as Error).message} (after ${retries} retries)`, retries }
        }
      }
      collected.push({ name: f.name, result })
      setResults([...collected])
    }
    setUploading(false)

    // Refresh the "Recent uploads" + "Per-carrier" + "Volume" panels
    qc.invalidateQueries({ queryKey: ['uploads'] })
    qc.invalidateQueries({ queryKey: ['uploads-per-carrier'] })
    qc.invalidateQueries({ queryKey: ['uploads-volume'] })
    qc.invalidateQueries({ queryKey: ['home'] })
    qc.invalidateQueries({ queryKey: ['carriers'] })
  }

  return (
    <div>
      {/* HEADER */}
      <div className="mb-6">
        <div className="text-[12px] font-semibold text-royal-600 uppercase tracking-wider">Module 1</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">{t('uploads.title')}</h1>
        <p className="text-ink-500 text-[14px] mt-2">{t('uploads.subtitle')}</p>
      </div>

      {/* TAB SWITCHER */}
      <div className="flex gap-1 mb-8 border-b border-ink-200">
        {(['upload', 'activity'] as Tab[]).map(tabId => (
          <button
            key={tabId}
            onClick={() => setActiveTab(tabId)}
            className={[
              'px-4 py-2 text-[13px] font-medium rounded-t-lg -mb-px border-b-2 transition-colors',
              activeTab === tabId
                ? 'border-royal-500 text-royal-700 bg-white'
                : 'border-transparent text-ink-500 hover:text-ink-700 hover:bg-ink-50',
            ].join(' ')}
          >
            {tabId === 'upload' ? (
              <span className="flex items-center gap-1.5"><Icon name="upload-cloud" className="w-3.5 h-3.5" />{t('uploads.upload_tab')}</span>
            ) : (
              <span className="flex items-center gap-1.5"><Icon name="list" className="w-3.5 h-3.5" />{t('uploads.invoice_activity_tab')}</span>
            )}
          </button>
        ))}
      </div>

      {/* INVOICE ACTIVITY TAB */}
      {activeTab === 'activity' && <InvoiceActivityTab />}

      {/* UPLOAD TAB */}
      {activeTab === 'upload' && <>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-5 mb-10">
        <KpiCard icon="check-circle-2" tone="mint"  label={t('uploads.files_14d')}   value={summary ? FMT_INT.format(summary.files_14d) : '—'} sub={t('uploads.ingested_cleanly')} />
        <KpiCard icon="loader"         tone="royal" label={t('uploads.last_import')}   value={lastUpload ? relativeTimeSince(lastUpload.imported_at) : '—'} sub={lastUpload?.carrier} />
        <KpiCard icon="alert-circle"   tone="flame" label={t('uploads.errors')}        value={summary ? FMT_INT.format(summary.rejected_files) : '—'} sub={t('uploads.rejected_files_sub')} />
        <KpiCard icon="database"       tone="sky2"  label={t('uploads.rows_ingested')} value={summary ? FMT_INT.format(summary.rows_24h) : '—'} sub={t('uploads.last_24h')} />
      </div>

      {/* DROP ZONE — single, prominent, working */}
      <Card className="p-6 mb-10">
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handlePicked(e.dataTransfer.files) }}
          className="rounded-2xl border-2 border-dashed border-ink-300 bg-ink-50 hover:bg-mint-50 hover:border-mint-400 transition p-12 text-center cursor-pointer"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl grid place-items-center bg-white border border-ink-200 mb-4">
            <Icon name="upload-cloud" className="w-7 h-7 text-mint-500" />
          </div>
          <div className="text-[16px] font-semibold">{t('uploads.drop_zone_title')}</div>
          <div className="text-[13px] text-ink-500 mt-1.5">USPS · DHL · FedEx · UPS · Evri · Asendia · GOFO · EPost · Intelcom · UniUni</div>
          <div className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-ink-900 text-white text-sm font-semibold">
            <Icon name="upload-cloud" /> {t('uploads.choose_files')}
          </div>
          <div className="mt-4 text-[12px] text-ink-500">{t('uploads.file_types')}</div>
        </div>
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          accept=".xlsx,.xlsb,.csv,.zip"
          multiple
          onChange={(e) => handlePicked(e.target.files)}
        />
        {(picked.length > 0 || results.length > 0 || uploading) && (
          <div className="mt-4 space-y-2">
            {uploading && (
              <div className="rounded-xl bg-royal-50 border border-royal-100 p-4 text-[13px] text-royal-700 flex items-center gap-2">
                <Icon name="loader" className="w-4 h-4 animate-spin" />
                {t('uploads.importing', { count: picked.length })}
              </div>
            )}
            {results.map((r) => (
              <ResultCard key={r.name} name={r.name} result={r.result} />
            ))}
          </div>
        )}
      </Card>

      {/* INGESTION VOLUME */}
      <Card className="p-6 mb-10">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[16px] font-semibold">Ingestion volume</div>
          <ChartPeriodPicker value={volumePeriod} onChange={setVolumePeriod} />
        </div>
        <div className="text-[12.5px] text-ink-500 mb-5">Rows imported per day across all carrier tables</div>
        {volume?.volume && volume.volume.length > 0 ? (
          <BarChart
            labels={volume.volume.map(v => v.day.slice(5))}
            data={volume.volume.map(v => v.n)}
            color="#34d399"
            height={220}
            yTickFormat={(v) => v >= 1000 ? Math.round(v / 1000) + 'k' : String(Math.round(v))}
            yMax={volYMax}
            barThickness={18}
          />
        ) : (
          <div className="h-[220px] grid place-items-center text-[13px] text-ink-500">No imports in the last 14 days.</div>
        )}
      </Card>

      {/* PER-CARRIER */}
      <Card className="p-6 mb-4">
        <div className="text-[16px] font-semibold mb-1">Per-carrier ingestion status</div>
        <div className="text-[12.5px] text-ink-500 mb-5">Row counts and last import per carrier table</div>
        <div className="grid grid-cols-5 gap-3">
          {perCarrier?.carriers.map(c => {
            const hasScraper = SCRAPER_CARRIERS.has(c.code)
            const isRunning = runScraper.isPending && runScraper.variables === c.code
            const isLogOpen = logCarrier === c.code
            return (
              <Card key={c.code} hover className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  {c.meta && (
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ background: c.meta.color }}>
                      <Icon name={c.meta.icon as IconName} className="w-4 h-4" />
                    </div>
                  )}
                  <div className="font-semibold text-[14px]">{c.meta?.name ?? c.code}</div>
                  <span className={`w-2 h-2 rounded-full ml-auto ${c.rows > 0 ? 'bg-mint-500' : 'bg-ink-300'}`} />
                </div>
                <div className="text-[11px] uppercase text-ink-500 tracking-wider">Total rows</div>
                <div className="text-[18px] font-bold num">{FMT_INT.format(c.rows)}</div>
                <div className="text-[11.5px] text-ink-500 mt-1">{c.last_imported ? relativeTimeSince(c.last_imported) : 'never'}</div>
                {hasScraper && (
                  <div className="mt-3 pt-3 border-t border-ink-100 flex gap-1">
                    <button
                      onClick={() => runScraper.mutate(c.code)}
                      disabled={isRunning}
                      className="flex-1 h-7 rounded-md bg-royal-50 hover:bg-royal-100 text-royal-700 text-[11.5px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-wait"
                    >
                      {isRunning ? '…' : '▶ Run'}
                    </button>
                    <button
                      onClick={() => setLogCarrier(isLogOpen ? null : c.code)}
                      className={[
                        'px-2.5 h-7 rounded-md text-[11.5px] font-medium transition-colors',
                        isLogOpen
                          ? 'bg-royal-100 text-royal-700'
                          : 'bg-ink-50 hover:bg-ink-100 text-ink-600',
                      ].join(' ')}
                      title="View logs"
                    >
                      Logs
                    </button>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      </Card>

      {/* SCRAPER LOG PANEL — shown when a carrier log toggle is open */}
      {logCarrier && (
        <ScraperLogsPanel
          carrier={logCarrier}
          onClose={() => setLogCarrier(null)}
          onRun={() => runScraper.mutate(logCarrier)}
          isRunning={runScraper.isPending && runScraper.variables === logCarrier}
        />
      )}

      <div className="mb-10" />

      {/* RECENT */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-100">
          <div className="text-[16px] font-semibold">Recent uploads</div>
          <div className="text-[12.5px] text-ink-500 mt-1">Last 14 days, across all carriers</div>
        </div>
        <table className="w-full text-[13.5px]">
          <thead className="bg-ink-50 text-ink-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold py-2.5 px-6">File</th>
              <th className="text-left font-semibold py-2.5">Carrier</th>
              <th className="text-right font-semibold py-2.5">Rows</th>
              <th className="text-left font-semibold py-2.5 pr-6">When</th>
            </tr>
          </thead>
          <tbody>
            {(!uploads || uploads.uploads.length === 0) && (
              <tr><td colSpan={4} className="py-10 text-center text-ink-500">No recent uploads.</td></tr>
            )}
            {uploads?.uploads.map(u => (
              <tr key={u.id} className="table-row border-t border-ink-100">
                <td className="py-3 px-6"><span className="mono text-[12.5px]">{u.file}</span></td>
                <td><span className="pill pill-ink">{u.carrier.toUpperCase()}</span></td>
                <td className="text-right num">{FMT_INT.format(u.rows)}</td>
                <td className="text-ink-500 pr-6">{relativeTime(u.age_minutes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      </> /* end upload tab */}
    </div>
  )
}

function relativeTimeSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  return relativeTime(Math.floor(ms / 60_000))
}

function ResultCard({ name, result }: { name: string; result: UploadResult }) {
  // Zip uploads return result.results = [{file, ok, …}]. Render the wrapper
  // card plus a nested card for each inner file.
  if (result.is_zip && result.results && result.results.length > 0) {
    const okCount = result.results.filter(r => r.ok).length
    const failCount = result.results.length - okCount
    const wrapperOk = failCount === 0
    return (
      <div className={`rounded-xl border p-4 ${wrapperOk ? 'bg-mint-50 border-mint-100' : 'bg-honey-50 border-honey-100'}`}>
        <div className="flex items-center gap-2 mb-3">
          <Icon name={wrapperOk ? 'check-circle-2' : 'alert-circle'} className={`w-4 h-4 ${wrapperOk ? 'text-mint-600' : 'text-honey-600'}`} />
          <div className="font-semibold text-[14px] mono">{name}</div>
          <span className="pill pill-ink">ZIP · {result.results.length} files</span>
          <span className="ml-auto text-[12px] text-ink-500">
            {okCount}/{result.results.length} succeeded
          </span>
        </div>
        <div className="space-y-2 pl-6 border-l-2 border-ink-100">
          {result.results.map((r) => <InnerFileCard key={r.file} r={r} />)}
        </div>
      </div>
    )
  }

  // Single-file: use the top-level fields
  return (
    <div className={`rounded-xl border p-4 ${result.ok ? 'bg-mint-50 border-mint-100' : 'bg-flame-50 border-flame-100'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon name={result.ok ? 'check-circle-2' : 'alert-circle'} className={`w-4 h-4 ${result.ok ? 'text-mint-600' : 'text-flame-600'}`} />
        <div className="font-semibold text-[14px] mono">{name}</div>
        {result.carrier && <span className="pill pill-ink">{result.label ?? result.carrier}</span>}
        {result.duration_ms !== undefined && (
          <span className="ml-auto text-[12px] text-ink-500">{(result.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </div>
      {result.ok ? (
        <ImporterStdout text={result.stdout ?? ''} />
      ) : (
        <>
          <div className="text-[13px] text-flame-700">{result.error ?? 'Import failed'}</div>
          {result.hint && <div className="text-[12px] text-ink-600 mt-1">{result.hint}</div>}
          {result.stderr && (
            <details className="mt-2">
              <summary className="text-[12px] text-ink-500 cursor-pointer">stderr</summary>
              <pre className="mt-1 text-[11.5px] mono bg-white p-2 rounded border border-ink-100 overflow-x-auto whitespace-pre-wrap">{result.stderr}</pre>
            </details>
          )}
        </>
      )}
    </div>
  )
}

function InnerFileCard({ r }: { r: PerFileResult }) {
  return (
    <div className={`rounded-lg p-3 ${r.ok ? 'bg-white border border-mint-100' : 'bg-white border border-flame-100'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon name={r.ok ? 'check-circle-2' : 'alert-circle'} className={`w-3.5 h-3.5 ${r.ok ? 'text-mint-600' : 'text-flame-600'}`} />
        <div className="font-semibold text-[13px] mono">{r.file}</div>
        {r.carrier && <span className="pill pill-ink">{r.label ?? r.carrier}</span>}
        {r.duration_ms !== undefined && (
          <span className="ml-auto text-[11.5px] text-ink-500">{(r.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </div>
      {r.ok ? (
        <ImporterStdout text={r.stdout ?? ''} />
      ) : (
        <>
          <div className="text-[12.5px] text-flame-700">{r.error ?? `Importer exited ${r.exit_code}`}</div>
          {r.stderr && (
            <details className="mt-1">
              <summary className="text-[11.5px] text-ink-500 cursor-pointer">stderr</summary>
              <pre className="mt-1 text-[11px] mono bg-ink-50 p-2 rounded border border-ink-100 overflow-x-auto whitespace-pre-wrap">{r.stderr}</pre>
            </details>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Scraper log panel
// ---------------------------------------------------------------------------

function fmtDur(ms: number | null): string {
  if (ms === null) return ''
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60_000)}m ${((ms % 60_000) / 1000).toFixed(0)}s`
}

function ScraperLogsPanel({
  carrier,
  onClose,
  onRun,
  isRunning,
}: {
  carrier: string
  onClose: () => void
  onRun: () => void
  isRunning: boolean
}) {
  const { data } = useQuery<{ runs: ScraperRun[] }>({
    queryKey: ['scraper-logs', carrier],
    queryFn: () => fetch(`/api/scrapers/${carrier}/logs`).then(r => r.json()),
    // Poll every 2 s while the latest run is in progress
    refetchInterval: (query) => {
      const runs = (query.state.data as { runs: ScraperRun[] } | undefined)?.runs ?? []
      return runs[0]?.status === 'running' ? 2000 : false
    },
  })

  const latest = data?.runs?.[0] ?? null
  const history = data?.runs?.slice(1) ?? []

  const statusCls = (s: ScraperRun['status']) =>
    s === 'running' ? 'text-royal-700 bg-royal-50 border-royal-200' :
    s === 'success' ? 'text-mint-700 bg-mint-50 border-mint-200' :
    s === 'skipped' ? 'text-ink-600 bg-ink-50 border-ink-200' :
                     'text-flame-700 bg-flame-50 border-flame-200'

  const carrierLabel = carrier.toUpperCase()

  return (
    <Card className="mb-10 overflow-hidden border border-ink-200">
      {/* Panel header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-ink-100 bg-ink-50">
        <span className="font-semibold text-[14px]">{carrierLabel} Scraper Logs</span>
        {(latest?.status === 'running' || isRunning) && (
          <Icon name="loader" className="w-4 h-4 animate-spin text-royal-600" />
        )}
        <div className="ml-auto flex gap-2">
          <button
            onClick={onRun}
            disabled={isRunning || latest?.status === 'running'}
            className="h-7 px-3 rounded-md bg-royal-600 hover:bg-royal-700 text-white text-[12px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-wait"
          >
            ▶ Run again
          </button>
          <button
            onClick={onClose}
            className="h-7 px-3 rounded-md bg-ink-200 hover:bg-ink-300 text-ink-700 text-[12px] font-medium transition-colors"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Content */}
      {!latest ? (
        <div className="px-5 py-10 text-center text-[13px] text-ink-500">
          No runs yet for this session. Click <strong>▶ Run</strong> on the card above to trigger the {carrierLabel} scraper.
        </div>
      ) : (
        <div className="p-5">
          {/* Latest run status row */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-semibold ${statusCls(latest.status)}`}>
              {latest.status === 'running' && <Icon name="loader" className="w-3 h-3 animate-spin" />}
              {latest.status.toUpperCase()}
            </span>
            <span className="text-[12.5px] text-ink-500">
              Started {new Date(latest.started_at).toLocaleString()}
            </span>
            {latest.duration_ms !== null && (
              <span className="text-[12.5px] text-ink-500">{fmtDur(latest.duration_ms)}</span>
            )}
            {latest.exit_code !== null && latest.exit_code !== 0 && (
              <span className="text-[12px] font-mono text-flame-700">exit {latest.exit_code}</span>
            )}
          </div>

          {/* stdout */}
          {latest.stdout ? (
            <details open={latest.status !== 'success'} className="mb-3">
              <summary className="text-[12px] text-ink-500 cursor-pointer select-none mb-1">stdout</summary>
              <pre className="text-[11.5px] font-mono bg-ink-900 text-ink-100 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-[300px] overflow-y-auto">{latest.stdout}</pre>
            </details>
          ) : latest.status === 'running' ? (
            <div className="text-[13px] text-ink-400 italic mb-3">Waiting for output…</div>
          ) : null}

          {/* stderr */}
          {latest.stderr && (
            <details open className="mb-3">
              <summary className="text-[12px] text-flame-600 cursor-pointer select-none mb-1">stderr</summary>
              <pre className="text-[11.5px] font-mono bg-flame-50 text-flame-800 border border-flame-200 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-[300px] overflow-y-auto">{latest.stderr}</pre>
            </details>
          )}

          {/* Previous runs */}
          {history.length > 0 && (
            <details className="mt-4">
              <summary className="text-[12px] text-ink-500 cursor-pointer select-none">
                Previous runs ({history.length})
              </summary>
              <table className="w-full mt-2 text-[12px]">
                <thead>
                  <tr className="text-ink-400 text-[11px] uppercase tracking-wider">
                    <th className="text-left py-1 font-semibold">Status</th>
                    <th className="text-left py-1 font-semibold">Started</th>
                    <th className="text-right py-1 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(r => (
                    <tr key={r.id} className="border-t border-ink-100">
                      <td className="py-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold border ${statusCls(r.status)}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-1.5 text-ink-600">{new Date(r.started_at).toLocaleString()}</td>
                      <td className="py-1.5 text-right text-ink-600">{fmtDur(r.duration_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </Card>
  )
}

function ImporterStdout({ text }: { text: string }) {
  // Try to extract the headline "read=X  inserted=Y  duplicates_skipped=Z" line
  const headline = text.match(/total_read=(\d+)\s+total_inserted=(\d+)\s+total_duplicates=(\d+)/)
                  ?? text.match(/read=(\d+)\s+inserted=(\d+)\s+duplicates_skipped=(\d+)/)
  return (
    <>
      {headline ? (
        <div className="text-[13.5px] text-mint-700">
          Imported <strong>{Number(headline[2]).toLocaleString()}</strong> new rows
          {' · '}<span className="text-ink-500">{Number(headline[3]).toLocaleString()} duplicates skipped</span>
          {' · '}<span className="text-ink-500">{Number(headline[1]).toLocaleString()} read</span>
        </div>
      ) : (
        <div className="text-[13px] text-mint-700">Import completed.</div>
      )}
      {text && (
        <details className="mt-2">
          <summary className="text-[12px] text-ink-500 cursor-pointer">Full log</summary>
          <pre className="mt-1 text-[11.5px] mono bg-white p-2 rounded border border-ink-100 overflow-x-auto whitespace-pre-wrap">{text}</pre>
        </details>
      )}
    </>
  )
}
